import { useEffect } from "react";
import { useSelector } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";

import { usePrefetchAdminAreaData } from "@/api/adminArea/usePrefetchAdminAreaData";
import { useGetFeaturesAPI } from "@/api/features/useFeaturesAPI";
import { FirstLoginOnboardingDialog } from "@/components/onboarding/FirstLoginOnboardingDialog";
import { OnboardingTour } from "@/components/onboarding/OnboardingTour";
import SideBar from "@/components/sidebar";
import LoadingPage from "@/components/ui/LoadingPage";
import Overlay from "@/components/ui/overlay";
import { MapProvider } from "@/context/MapProvider";
import { useAgenticWorkspaceSwap } from "@/hooks/agenticAI/useAgenticWorkspaceSwap";
import { useAuth } from "@/hooks/auth/useAuth";
import { useSsoAutoStart } from "@/hooks/auth/useSsoAutoStart";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { isGlobalLogoutPending } from "@/utils/sso";

const ProtectedRoute = () => {
  const { isOnboardingModuleEnabled, isGranularRbacEnabled } = useAppFeatures();
  const { isAuthenticating, isAuthenticated, isAuthChecked, hasLoggedIn } =
    useAuth();

  useAgenticWorkspaceSwap(!!isAuthenticated);

  // Warm country list + admin-area hierarchy caches in the background
  // once authenticated, so dependent UI renders instantly
  usePrefetchAdminAreaData(!!isAuthenticated);

  // Load plan modules early so module gates don't wait on first visit
  useGetFeaturesAPI(null, {
    enabled: !!isAuthenticated && isGranularRbacEnabled
  });

  const { showComparisonPopup, isDrawPolygon, showLoadingOverlay } =
    useSelector((state: RootState) => state.ui);
  const navigate = useNavigate();

  // Unauthenticated, and the initial auth query is no longer in flight. Gating on
  // `isAuthChecked` prevents acting while the status call is still running.
  const isUnauthenticated =
    (!hasLoggedIn || isAuthChecked) && !isAuthenticating && !isAuthenticated;

  // With LOKASI SSO on, an unauthenticated protected route hands off to the IdP
  // rather than showing a login screen. This also silently recovers an expired
  // app session: the IdP session usually outlives it, so /authorize 302s
  // straight back to our callback with no prompt.
  const { isRedirecting: isRedirectingToSso } =
    useSsoAutoStart(isUnauthenticated);

  // Fall back to the local login page when SSO is off or unavailable. A pending
  // global logout also leaves `isRedirectingToSso` false, but the browser is
  // already headed to the IdP logout page — routing to `/login` would flash the
  // local login page mid hand-off.
  useEffect(() => {
    if (isUnauthenticated && !isRedirectingToSso && !isGlobalLogoutPending()) {
      navigate("/login", {
        replace: true
      });
    }
  }, [navigate, isUnauthenticated, isRedirectingToSso]);

  // check the !isAuthenticated to wait the navigation to login page
  if (isAuthenticating || !isAuthenticated) {
    return <LoadingPage />;
  }

  return (
    <MapProvider>
      <div className="flex h-screen text-[12px]">
        {showLoadingOverlay && <Overlay />}
        {!showComparisonPopup.isShow && !isDrawPolygon && <SideBar />}
        <div className="flex flex-col flex-grow overflow-hidden">
          <Outlet />
        </div>
        {isOnboardingModuleEnabled && (
          <>
            <FirstLoginOnboardingDialog />
            <OnboardingTour />
          </>
        )}
      </div>
    </MapProvider>
  );
};

export default ProtectedRoute;
